import React, { Suspense } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Cpu, Target, Sparkles, Activity, BookOpen, TrendingUp } from 'lucide-react';
import { useApp } from '../context/AppContext';
import ModeToggle from '../components/ModeToggle';
import DailyQuestionsButton from '../components/DailyQuestionsButton';
import TestDayCountdown from '../components/TestDayCountdown';

const Spline = React.lazy(() => import('@splinetool/react-spline'));

const ROBOT_IMG = 'https://customer-assets-jt897jd0.emergentagent.net/job_63727204-8e2f-45b7-8117-c2998f36774d/artifacts/aaq1h17c_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2009_58_40%20PM.png';
const SPLINE_SCENE = 'https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode';

export default function CyberHome() {
  const { user, toggleMode, logout } = useApp();
  const nav = useNavigate();

  const capabilities = [
    { icon: Cpu, title: 'PULSE AI TUTOR', body: 'Claude-powered tutor that adapts to your weak spots.', stat: '1:1', onClick: () => window.dispatchEvent(new CustomEvent('pp:open-chatbot')) },
    { icon: Activity, title: 'DAILY QUEST', body: 'Five hand-picked questions a day. Streaks that ignite.', stat: '24h', onClick: () => nav('/daily') },
    { icon: Target, title: 'SCORE PREDICTOR', body: 'Ten calibrated questions. Instant predicted range.', stat: '10Q', onClick: () => nav('/predictor') },
    { icon: BookOpen, title: 'QUESTION BANK', body: 'Curated Math / Reading / Writing / Science. Generate more with AI.', stat: '∞', onClick: () => nav('/bank') },
    { icon: Sparkles, title: 'DUAL MODE', body: 'Cyber Mode for grind sessions. StoryBook Land when you need whimsy.', stat: '2x', onClick: toggleMode },
    { icon: TrendingUp, title: 'STUDY PLAN', body: '5-week plan that scales rigor with your target score.', stat: '5w', onClick: () => nav('/study-plan') },
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* NAV DOCK */}
      <motion.nav
        initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
        className="fixed top-3 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1.5 px-2 py-1.5 rounded-full glass-cyber cyber-glow max-w-[calc(100vw-1rem)] flex-wrap justify-center"
      >
        <div data-testid="nav-logo" className="px-2 font-orbitron tracking-widest text-[#00FFD1] text-xs">
          PREP<span className="text-white">PULSE</span>
        </div>
        <div className="h-5 w-px bg-[#00FFD1]/30 hidden sm:block" />
        <div className="hidden md:flex items-center gap-0">
          {[
            ['Home', '/'],
            ['Predictor', '/predictor'],
            ['Plan', '/study-plan'],
            ['Bank', '/bank'],
          ].map(([label, path]) => (
            <button key={path} data-testid={`nav-${label.toLowerCase()}`}
              onClick={() => nav(path)}
              className="px-2.5 py-1 text-xs font-mono-cyber text-neutral-300 hover:text-[#00FFD1] transition-colors">
              {label}
            </button>
          ))}
        </div>
        <ModeToggle />
        {user ? (
          <button data-testid="nav-logout" onClick={logout}
            className="px-2 py-1 text-xs font-mono-cyber text-neutral-400 hover:text-white">logout</button>
        ) : (
          <button data-testid="nav-signin" onClick={() => nav('/auth')}
            className="px-3 py-1 text-xs font-orbitron tracking-widest bg-[#00FFD1] text-black rounded-full">
            SIGN IN
          </button>
        )}
        {user?.is_admin && (
          <button data-testid="nav-admin" onClick={() => nav('/admin')}
            className="px-3 py-1 text-xs font-orbitron tracking-widest border border-[#FF3366] text-[#FF3366] rounded-full">
            ADMIN
          </button>
        )}
      </motion.nav>

      {/* HERO */}
      <section className="relative min-h-screen w-full flex items-center scanlines">
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />

        {/* User-turnable Spline scene (default orbit controls) */}
        <div className="absolute inset-0 z-0" data-testid="spline-hero">
          <Suspense fallback={
            <div className="w-full h-full flex items-center justify-center">
              <img src={ROBOT_IMG} alt="Pulse" className="max-h-[70vh] opacity-80" />
            </div>
          }>
            <Spline scene={SPLINE_SCENE} />
          </Suspense>
          {/* Turn hint */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono-cyber text-[10px] text-[#00FFD1]/70 tracking-widest pointer-events-none">
            ↻ click & drag to turn the robot
          </div>
        </div>

        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black via-black/60 to-transparent pointer-events-none" />

        <div className="relative z-10 max-w-3xl px-8 lg:px-20 pt-24 pointer-events-none">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
            className="[&_button]:pointer-events-auto [&_a]:pointer-events-auto">
            <div className="font-mono-cyber text-xs text-[#00FFD1] tracking-widest mb-4">// ACT PREP // NEURAL ENGINE v2.6</div>
            <h1 className="font-orbitron font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.05] cyber-text-glow">
              MASTER<br />THE ACT.<br />
              <span className="text-[#00FFD1]">SILICON PRECISION.</span>
            </h1>
            <p className="mt-6 text-neutral-300 text-lg max-w-xl">
              PrepPulse is a dual-mode tutor. In Cyber Mode, we drill you like a surgeon.
              Flip to StoryBook Land when you need a break — same rigor, different world.
            </p>
            <div className="mt-10 grid gap-4 max-w-md">
              <TestDayCountdown variant="cyber" />
              <DailyQuestionsButton variant="cyber" />
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="cta-predictor" onClick={() => nav('/predictor')}
                  className="glass-cyber rounded-xl px-4 py-3 text-left hover:cyber-glow-strong transition-shadow">
                  <TrendingUp className="text-[#00FFD1] mb-1" size={18} />
                  <div className="font-orbitron text-xs tracking-wider">SCORE PREDICTOR</div>
                </button>
                <button data-testid="cta-studyplan" onClick={() => nav('/study-plan')}
                  className="glass-cyber rounded-xl px-4 py-3 text-left hover:cyber-glow-strong transition-shadow">
                  <Target className="text-[#00FFD1] mb-1" size={18} />
                  <div className="font-orbitron text-xs tracking-wider">STUDY PLAN</div>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES BENTO — all cards clickable */}
      <section className="relative px-8 lg:px-20 py-24 border-t border-[#00FFD1]/10">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="font-mono-cyber text-xs text-[#00FFD1] mb-3">// CAPABILITIES</div>
            <h2 className="font-orbitron text-4xl lg:text-5xl">Built for the score you want.</h2>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {capabilities.map((c, i) => (
              <motion.button key={i}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                onClick={c.onClick}
                whileHover={{ y: -4 }}
                data-testid={`capability-${c.title.toLowerCase().replace(/ /g,'-')}`}
                className="glass-cyber rounded-2xl p-6 text-left hover:cyber-glow-strong transition-shadow cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <c.icon className="text-[#00FFD1]" size={22} />
                  <div className="font-mono-cyber text-2xl text-[#00FFD1]">{c.stat}</div>
                </div>
                <div className="font-orbitron text-sm tracking-widest">{c.title}</div>
                <div className="text-neutral-400 text-sm mt-2">{c.body}</div>
                <div className="mt-4 font-mono-cyber text-[10px] text-[#00FFD1]/70">&gt; open →</div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* TERMINAL SIGNUP */}
      <section className="relative px-8 lg:px-20 py-24 border-t border-[#00FFD1]/10">
        <div className="max-w-2xl mx-auto glass-cyber rounded-2xl p-8">
          <div className="font-mono-cyber text-[#00FFD1] text-xs mb-2">// terminal_v2.6 :: initialize_user</div>
          <div className="font-orbitron text-2xl mb-6">$ create_account --priority=high</div>
          <p className="text-neutral-400 mb-6">
            Track daily quests, save study plans, unlock Pulse memory.
            Two options: password or Google Sign-In.
          </p>
          <button data-testid="terminal-signup" onClick={() => nav('/auth')}
            className="px-6 py-3 bg-[#00FFD1] text-black font-orbitron tracking-widest hover:bg-white transition-colors rounded-lg">
            &gt; PROCEED
          </button>
        </div>
      </section>

      <footer className="px-8 lg:px-20 py-8 border-t border-[#00FFD1]/10 flex justify-between font-mono-cyber text-xs text-neutral-500">
        <div>PREPPULSE // {new Date().getFullYear()}</div>
        <div>// engineered for the 36</div>
      </footer>
    </div>
  );
}
